import { TFunction } from 'i18next';
import { JOURNEY_STATE } from '../../../App.state';
import { RouterHelpers } from '../../../router/router-helpers';

export type WizardAnalyticsLinkName =
  | 'Match Maker'
  | 'Financing'
  | 'Dealer Selection'
  | 'Contact'
  | 'Summary'
  | 'Confirmation';

type HeroTitleNamespace = 'match-maker' | 'scaffolding' | 'personal-information';

export interface JourneyStepDefinition {
  status: JOURNEY_STATE;
  link: string;
  additionalPaths?: string[];
  wizardLabelKey: string;
  analyticsLinkName: WizardAnalyticsLinkName;
  heroTitleKey: string;
  heroTitleNamespace: HeroTitleNamespace;
}

export const JOURNEY_STEP_DEFINITIONS: JourneyStepDefinition[] = [
  {
    status: JOURNEY_STATE.STARTED,
    link: RouterHelpers.getMatchMakerQuestionPage1Path(),
    additionalPaths: [RouterHelpers.getMatchMakerQuestionPage1EditPath()],
    wizardLabelKey: 'wizard.match-maker',
    analyticsLinkName: 'Match Maker',
    heroTitleKey: 'hero-teaser.title-step-one',
    heroTitleNamespace: 'match-maker'
  },
  {
    status: JOURNEY_STATE.FINANCING,
    link: RouterHelpers.getMatchMakerQuestionPage2Path(),
    additionalPaths: [RouterHelpers.getMatchMakerQuestionPage2EditPath()],
    wizardLabelKey: 'wizard.financing',
    analyticsLinkName: 'Financing',
    heroTitleKey: 'hero-teaser.title-step-two',
    heroTitleNamespace: 'match-maker'
  },
  {
    status: JOURNEY_STATE.DEALER_SELECTION,
    link: RouterHelpers.getDealerSelectionPagePath(),
    additionalPaths: [RouterHelpers.getDealerSelectionEditPagePath()],
    wizardLabelKey: 'wizard.dealer-selection',
    analyticsLinkName: 'Dealer Selection',
    heroTitleKey: 'hero-teaser.title-step-three',
    heroTitleNamespace: 'match-maker'
  },
  {
    status: JOURNEY_STATE.CONTACT_DETAILS,
    link: RouterHelpers.getPersonalDetailsPath(),
    wizardLabelKey: 'wizard.contact',
    analyticsLinkName: 'Contact',
    heroTitleKey: 'hero-teaser.title-step-four',
    heroTitleNamespace: 'match-maker'
  },
  {
    status: JOURNEY_STATE.SUMMARY,
    link: RouterHelpers.getSummaryPagePath(),
    wizardLabelKey: 'wizard.summary',
    analyticsLinkName: 'Summary',
    heroTitleKey: 'hero-teaser.title-step-five',
    heroTitleNamespace: 'match-maker'
  },
  {
    status: JOURNEY_STATE.CONFIRMATION,
    link: RouterHelpers.getConfirmationPagePath(),
    wizardLabelKey: 'wizard.confirmation',
    analyticsLinkName: 'Confirmation',
    heroTitleKey: 'hero-teaser.title-step-six',
    heroTitleNamespace: 'match-maker'
  }
];

export const normalizeJourneyStatus = (currentJourneyStatus: JOURNEY_STATE): JOURNEY_STATE => {
  if (currentJourneyStatus === JOURNEY_STATE.MM_FINISHED) {
    return JOURNEY_STATE.FINANCING;
  }

  return currentJourneyStatus;
};

export const getJourneyStatusIndex = (currentJourneyStatus: JOURNEY_STATE): number => {
  const normalizedStatus = normalizeJourneyStatus(currentJourneyStatus);
  const stepIndex = JOURNEY_STEP_DEFINITIONS.findIndex((step) => step.status === normalizedStatus);

  return stepIndex >= 0 ? stepIndex : 0;
};

const normalizePathname = (pathname: string): string => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
};

export const getJourneyStepDefinitionByPathname = (
  pathname: string
): JourneyStepDefinition | undefined => {
  const normalizedPathname = normalizePathname(pathname);

  return JOURNEY_STEP_DEFINITIONS.find(
    (step) => step.link === normalizedPathname || step.additionalPaths?.includes(normalizedPathname)
  );
};

export const getJourneyStepDefinitionByStatus = (
  currentJourneyStatus: JOURNEY_STATE
): JourneyStepDefinition => {
  const normalizedStatus = normalizeJourneyStatus(currentJourneyStatus);

  return (
    JOURNEY_STEP_DEFINITIONS.find((step) => step.status === normalizedStatus) ??
    JOURNEY_STEP_DEFINITIONS[0]
  );
};

export const getHeroTeaserTitle = (
  t: TFunction,
  pathname: string,
  currentJourneyStatus: JOURNEY_STATE
): string => {
  const stepDefinition =
    getJourneyStepDefinitionByPathname(pathname) ??
    getJourneyStepDefinitionByStatus(currentJourneyStatus);

  return t(stepDefinition.heroTitleKey, { ns: stepDefinition.heroTitleNamespace }) as string;
};
